import { useScheduleQueue } from '@/composables/useScheduleQueue';
import { useDoubleBook } from '@/composables/useDoubleBook';
import { useSeatReminder } from '@/composables/useSeatReminder';
import RemoveFeedbackModal from '~/components/fragments/dialogs/remove-feedback.vue';
import SeatStatusModal from '~/components/fragments/dialogs/seat-status.vue';
import SeatFullModal from '~/components/fragments/dialogs/seat-full.vue';
import SeatRemoveConfirmModal from '~/components/fragments/dialogs/seat-remove-confirm.vue';
import SeatConflictBlockedModal from '~/components/fragments/dialogs/seat-conflict-blocked.vue';
import FindASessionModal from '~/components/fragments/dialogs/find-a-session.vue';
import DoubleBookUnknownModal from '~/components/fragments/dialogs/double-book-unknown.vue';
import ConflictRemovedModal from '~/components/fragments/dialogs/conflict-removed.vue';

// Single source of truth for the "Add to My Schedule" (and Save-a-Seat display
// state) behavior shared by the agenda cards and the session drawer. Extracted
// verbatim from the three cards so behavior stays identical everywhere.
//
// sessionRef: a Ref/computed of the session object.
// opts.extraSaved: getter for a consumer-specific "already saved" truth beyond
//   direct membership in user.scheduled (e.g. user-event cards are always saved).
export function useScheduleAction(sessionRef, { extraSaved = () => false } = {}) {
    const { loggedIn, user, fetch: refreshSession } = useUserSession();
    const userApi = useUserApi();
    const { $bus } = useNuxtApp();
    const { enqueue } = useScheduleQueue();
    const doubleBook = useDoubleBook();
    const seatReminder = useSeatReminder();
    const { open, openHandle } = useModal();
    const removeFeedback = useRemoveFeedback();
    const scheduleConflicts = useScheduleConflicts();
    // Gates the already-full interception below: a full session is only worth
    // interrupting an add for once seats can actually be claimed.
    const { showSeatReservations } = useScheduleControls();

    // "View My Schedule" on the seat outcome screens emitted `view-schedule` and
    // closed itself, and nothing anywhere listened — so the button read as a
    // second Close. useModal hands its props straight to the component and Vue
    // resolves an `onX` prop against the declared emits, so this IS the listener.
    // Spread into every screen that renders the button (both outcomes, from both
    // the add and the seat gesture); the processing screen has no buttons at all.
    // Same URL the header's My Schedule link uses, so the two cannot drift.
    const viewSchedule = { onViewSchedule: () => navigateTo('/agenda?tab=myschedule') };

    // Optimistic ref (set while a click is in flight / just resolved) wins over
    // the session prop so the button flips instantly instead of waiting for a refetch.
    const optimisticAdded = ref(null);
    const busy = ref(false);

    // Authoritative per-user source: direct membership in user.scheduled.
    const isScheduled = computed(() =>
        (user.value?.scheduled || []).includes(sessionRef.value?.unique_id)
    );
    const addButtonSaved = computed(() =>
        optimisticAdded.value !== null
            ? optimisticAdded.value
            : (isScheduled.value || extraSaved())
    );
    // Real per-user seat state lives on the auth session cookie
    // (user.sessionRegistration, mirrored client-side like user.scheduled).
    // The session prop's user_has_seat is only the dev randomize sim — kept as a fallback.
    //
    // These lists come from Bizzabo and hold BIZZABO session ids, so they must
    // be compared against the reconciled `bizzabo_session_id`, not `unique_id`
    // (a Sessionboard friendly_id — the two never matched, so this always read
    // false and the button never showed a saved seat). Sessions with no
    // confident match have a null id; guard so a null never equals a null.
    const bizzaboId = computed(() => sessionRef.value?.bizzabo_session_id ?? null);
    // Only a limited-capacity session has a seat to save. `sessionRegistration`
    // is every Bizzabo registration the attendee holds, and the schedule sync
    // now mirrors unlimited sessions up there too — so without this gate most of
    // their schedule read as "seat saved". That showed the saved-seat treatment
    // on ordinary sessions and, because the remove confirmation only opens when a
    // seat is held, told them a session with no capacity limit "requires a saved
    // seat in order to attend" (UNB-733).
    const isSeatSession = computed(() => sessionRef.value?.session_reservations === true);
    const seatButtonSaved = computed(() =>
        isSeatSession.value
        && (
            (!!bizzaboId.value && (user.value?.sessionRegistration || []).includes(bizzaboId.value))
            || !!sessionRef.value?.user_has_seat
        )
    );
    // Third state: Bizzabo puts a full-session request on a waitlist (202) and
    // reports it back under properties.sessionWaitlist. Without this the button
    // reads "Save a Seat" again after a reload and invites a duplicate request.
    const seatButtonWaitlisted = computed(() =>
        isSeatSession.value
        && !seatButtonSaved.value
        && !!bizzaboId.value
        && (user.value?.sessionWaitlist || []).includes(bizzaboId.value)
    );

    // "Find New Session" from the full-session modals: hand the attendee to the
    // existing Find a Session experience in gap mode, scoped to the slot the
    // session they wanted would have occupied. `windowed` because this is an
    // open span they chose rather than a gap bounded by scheduled sessions —
    // the server then suggests anything that FITS the window instead of
    // requiring an exact-gap match.
    //
    // Mirrors agenda.vue's openFindASession, including the prefs gate: the
    // modal opens on its preferences view when nothing is saved yet, and a
    // failed lookup is treated as "no prefs" rather than blocking the user.
    const openFindASessionForSlot = async (session) => {
        if (!session?.start_date_and_time || !session?.end_date_and_time) return;

        let prefsText = '';
        try {
            const res = await $fetch('/api/v1/schedule/preferences');
            prefsText = res?.prefs || '';
        } catch {
            prefsText = '';
        }

        await open(FindASessionModal, {
            mode: 'gap',
            gap: {
                date: session.start_date_and_time.slice(0, 10),
                start: session.start_date_and_time,
                end: session.end_date_and_time,
                windowed: true,
                // The session this is offering to replace. Without it the server
                // cannot tell the slot being vacated from one the attendee wants
                // to keep: it is still on their schedule, so it occupies exactly
                // the window being filled and every candidate in that slot was
                // rejected as overlapping. "Nothing matched your preferences" —
                // and then three matches once the session was removed by hand
                // (UNB-729).
                replacing: session.unique_id,
            },
            needsPrefs: !prefsText.trim(),
        });

        // A session added inside the modal changes My Schedule, so tell the
        // surrounding surface to resync the way an add/remove would.
        await refreshSession();
        $bus?.emit?.('schedule:changed', { session, removed: false });
    };

    // Add to My Schedule with no seat attached — the "add it anyway" branch of
    // the full-session modals. Kept separate from handleAdd so it cannot
    // re-enter the already-full interception that opened the modal in the
    // first place.
    const addWithoutSeat = async (session) => {
        try {
            busy.value = true;
            const conflict = await enqueue(async () => {
                const res = await userApi.addToSchedule(session.unique_id);
                if (res?.conflict) return res.conflict;
                optimisticAdded.value = true;
                await refreshSession();
                return null;
            });
            if (conflict) {
                doubleBook.show(session, conflict);
                return;
            }
            $bus?.emit?.('schedule:changed', { session, removed: false });
        } catch (err) {
            optimisticAdded.value = null;
            if (err?.statusCode === 401 || err?.response?.status === 401 || err?.data?.statusCode === 401) {
                await refreshSession();
                await navigateTo('/login');
            } else {
                open({
                    headline: 'We could not add that session',
                    body: 'Please try again in a moment.',
                });
            }
        } finally {
            busy.value = false;
        }
    };

    // The two ways out of the full-session modals (both variants share them).
    // Dismissing resolves null and correctly does nothing.
    const handleSeatFullChoice = async (choice, session) => {
        if (choice === 'find-session') {
            await openFindASessionForSlot(session);
        } else if (choice === 'add-to-schedule') {
            // "Add it anyway, without a seat." Only meaningful if it isn't
            // already on the schedule — the seat-failed path can be reached
            // from a session that was added moments earlier.
            if (!addButtonSaved.value) await addWithoutSeat(session);
        }
    };

    // An UNEXPECTED double-book: Bizzabo holds a registration for a session that
    // isn't on this attendee's schedule, so the refusal names something they
    // cannot see. The endpoint writes nothing and hands back the clashing session
    // instead, because neither way out is obviously right and both are
    // destructive in one direction — releasing a seat on a full session can't be
    // undone, and adding to the schedule is a write they didn't ask for.
    //
    // Shared by BOTH gestures. Adding registers too (rules 3 and 6), so an add
    // can raise this exactly as a seat request can — and handleAdd used to report
    // only 'saved' and 'waitlisted', so it swallowed this silently: the session
    // landed on the schedule, the card went action-required, and nothing said why
    // (Jack, 2026-08-20).
    const resolveUnexpectedDoubleBook = async (session, outcome) => {
        // AWAITED: the modal resolves which choice was taken, and both are
        // actions. Opening it without awaiting leaves them inert, the same trap
        // the full-session screen hit.
        const choice = await open(DoubleBookUnknownModal, {
            sessionTitle: session.title,
            clash: outcome.clash,
        });

        if (choice === 'take-this') {
            // Release the clashing registration, then retry this one. Serialized
            // like every other mutation, for the same reason.
            const swap = await enqueue(async () => {
                await userApi.unreserve(outcome.clash.id);
                const res = await userApi.reserve(session.id);
                await refreshSession();
                return res;
            });
            if (swap?.key === 'saved') {
                open(SeatStatusModal, { state: 'saved', sessionTitle: session.title, ...viewSchedule });
            } else if (swap?.key === 'waitlisted') {
                open(SeatStatusModal, { state: 'waitlisted', sessionTitle: session.title, ...viewSchedule });
            } else {
                open({ headline: swap?.title || 'We could not save your seat', body: swap?.copy || '' });
            }
            return;
        }

        if (choice === 'keep-other') {
            // A SWAP, not a conditional rollback. The two overlap by definition —
            // that is why we are here — and the schedule cannot hold both:
            // /api/v1/schedule/add returns a conflict WITHOUT inserting, and
            // Bizzabo refuses the overlap too. Keeping one always means giving up
            // the other, whether this gesture put it there moments ago or it was
            // already on the schedule.
            //
            // Release BEFORE dropping the schedule row, the order the remove path
            // uses, so a registration can never outlive its schedule entry — the
            // exact drift this flow exists to clean up. Best-effort: there may be
            // nothing registered, since the reservation that brought us here failed.
            try {
                await userApi.unreserve(session.id);
            } catch (relErr) {
                console.error('[seat] release on swap failed', relErr?.message || relErr);
            }
            await userApi.removeFromSchedule(session.unique_id);
            optimisticAdded.value = false;

            const added = await userApi.addToSchedule(outcome.clash.unique_id);
            await refreshSession();

            // Something ELSE on the schedule overlaps it. Say so rather than
            // appearing to do nothing, which is the failure this flow exists to stop.
            if (added?.conflict) {
                doubleBook.show(outcome.clash, added.conflict);
                return;
            }

            $bus?.emit?.('schedule:changed', { session: outcome.clash, removed: false });
        }
    };

    // `gesture` distinguishes the two ways a seat-holding session gets removed:
    // the schedule button ('schedule') and the seat button ('seat'). Both do the
    // same thing; the follow-up copy leads with whichever was pressed.
    const handleAdd = async (e, { gesture = 'schedule' } = {}) => {
        const session = sessionRef.value;
        if (!session) return;
        if (!loggedIn.value) {
            await navigateTo('/login');
            return;
        }

        // Hidden bulk-clear: Option(Alt)+Shift while clicking an already-added
        // session's Remove button wipes EVERY non-created session from the
        // schedule in one shot — an easy way to reset a schedule. Created
        // "My Events" live in a separate store (/api/v1/schedule/events) and are
        // never in user.scheduled, so they survive. Silent by design (no confirm).
        if (e?.altKey && e?.shiftKey && addButtonSaved.value) {
            const ids = [...(user.value?.scheduled || [])];
            if (!ids.length) return;
            try {
                busy.value = true;
                optimisticAdded.value = false; // this session is one of them — flip now
                await enqueue(async () => {
                    for (const id of ids) {
                        try { await userApi.removeFromSchedule(id); } catch { /* keep clearing the rest */ }
                    }
                    await refreshSession();
                    return null;
                });
                $bus?.emit?.('schedule:changed', { session, removed: true, cleared: true });
            } catch (err) {
                optimisticAdded.value = null;
            } finally {
                busy.value = false;
            }
            return;
        }

        // Adding a session that is already FULL. Nick asked for the explanation
        // back on this path ([Agenda] [Session is Full], Figma 4669:56090): the
        // attendee can still add it and take the onsite standby line, but they
        // should be told a seat cannot be saved and offered something else in
        // the slot first.
        //
        // This is NOT the refusal rule 8 removed. That turned people away; this
        // explains and then lets them through — "Add to My Schedule" on the modal
        // is a real way out, and dismissing simply does nothing.
        //
        // Same condition as hiding the Save a Seat button in the sidebar: only
        // when the attendee holds nothing. With the session already scheduled, a
        // seat, or a waitlist place, this gesture is a REMOVAL and must not be
        // intercepted.
        if (
            !addButtonSaved.value
            && showSeatReservations.value
            && session.session_reservations === true
            && !!session.is_full
            && !seatButtonSaved.value
            && !seatButtonWaitlisted.value
        ) {
            // A CLASH OUTRANKS THE FULL EXPLANATION (Nick, Asana 1217708959827710). Both are
            // reasons this add cannot proceed as asked, but they are not equal:
            // "no seat can be saved, add it anyway and join the standby line" is
            // advice about a session the attendee CAN still have, while a clash
            // means they cannot be in two places at once. Leading with the
            // full-session modal buried that — the attendee resolved it, added
            // the session, and only then learnt it overlapped something.
            //
            // Checked rather than inferred from the add, because this branch
            // returns before any add is attempted. If the check itself fails we
            // fall through to the full-session modal: the add behind it re-runs
            // the same test server-side and still refuses, so the clash is caught
            // either way and the worst case is the old ordering.
            let clash = null;
            try {
                busy.value = true;
                ({ conflict: clash } = await userApi.checkConflict(session.unique_id));
            } catch {
                clash = null;
            } finally {
                busy.value = false;
            }
            if (clash) {
                doubleBook.show(session, clash);
                return;
            }

            const choice = await open(SeatFullModal, {
                sessionTitle: session.title,
                variant: 'already-full',
                alreadyScheduled: false,
            });

            // "Add to My Schedule and Join Waitlist" — the modal's affirmative,
            // and ONE gesture with two effects. handleSeat is what performs it:
            // it adds the session when it isn't scheduled, asks Bizzabo for the
            // place, and reports back as a waitlist rather than a seat. Reached
            // here it has already been confirmed, hence standbyConfirmed.
            //
            // Nick, 2026-09-08: this used to fall through to
            // handleSeatFullChoice, which knows 'find-session' and
            // 'add-to-schedule' and nothing else — so the press closed the modal
            // and did nothing whatsoever.
            if (choice === 'join-standby') {
                await handleSeat(e, { standbyConfirmed: true });
                return;
            }

            await handleSeatFullChoice(choice, session);
            return;
        }

        // Recommended-session removal feedback (Asana UNB-599): before removing
        // an assistant-recommended session, surface the feedback modal. It NEVER
        // blocks the removal — Submit and dismiss both fall through to the normal
        // remove below — so this only records whether a reason was captured.
        // Suppressed after two consecutive no-response closes; never fires on an
        // add or on the alt+shift bulk-clear (handled and returned above).
        // Asked on EVERY removal of a recommended session (UNB-622). This used to
        // go quiet after two consecutive dismissals; Steph asked for it each time.
        // Does this removal also give up a seat (or a waitlist place)? Nick's
        // comps treat the two as ONE operation — both remove modals carry the
        // same confirm label and each notice says removing one removes the
        // other — so a seat has to be released alongside the schedule entry.
        const holdsSeat = seatButtonSaved.value || seatButtonWaitlisted.value;

        if (addButtonSaved.value && removeFeedback.isRecommended(session.unique_id)) {
            // The feedback modal DOUBLES as the confirmation when the Assistant
            // put this session there (Jack, 2026-08-17), so a recommended
            // session never stacks two dialogs.
            //
            // Dismissing it does NOT cancel the removal, seat or no seat. It
            // used to when a seat was at stake, on the reasoning that a scarce
            // seat shouldn't go to an accidental dismissal — but the modal has
            // no control that says "keep it": Submit and Close are its only
            // actions, and its own contract is that the removal happens either
            // way. So the cancel was behaviour with no affordance, and the
            // attendee had already pressed the button to unsave. Reversed at
            // Bri's request (Asana 1217770383805166): "the feedback loop
            // shouldn't be mandatory".
            const outcome = await open(RemoveFeedbackModal, { session });
            if (outcome?.submitted) {
                await removeFeedback.recordSubmit({ session, reasons: outcome.reasons, otherText: outcome.otherText });
            }
        } else if (addButtonSaved.value && holdsSeat) {
            // Not assistant-recommended, so the design's own confirmation runs.
            // `seat` vs `schedule` only changes the wording to match the button
            // they pressed — the operation is identical either way.
            //
            // Keyed on the GESTURE, not on whether a seat is held (Nick, Asana
            // 1217708959827718). Those are different questions, and reading the
            // second to answer the first meant pressing "Remove From My Schedule"
            // on a session whose seat was saved — the only case that reaches this
            // branch at all — always produced the seat wording. The schedule
            // variant was therefore unreachable from the schedule button.
            // Whether this removal settles the schedule or leaves work behind
            // (Nick, Asana 1217708959827768 / 827761 / 827745 / 827738).
            //
            // A clash needs at least two items, so exactly two conflicted items
            // anywhere means one clash between this session and one other —
            // removing either ends it. Any larger number means either a bigger
            // pile-up or a second clash elsewhere, and in both cases something is
            // still left to do afterwards.
            const conflictedIds = scheduleConflicts.conflictIds?.value;
            const conflict = !conflictedIds?.has(session.unique_id)
                ? 'none'
                : (conflictedIds.size === 2 ? 'resolves' : 'remains');

            const confirmed = await open(SeatRemoveConfirmModal, {
                sessionTitle: session.title,
                // A standby place is not a seat; saying "your saved seat" to
                // someone who only holds a place in line is untrue, not just
                // off-tone.
                variant: gesture !== 'seat'
                    ? (seatButtonWaitlisted.value ? 'schedule-waitlisted' : 'schedule')
                    : (seatButtonWaitlisted.value ? 'standby' : 'seat'),
                conflict,
            });

            // 'learn' is the Learn More button, which replaces Cancel while a
            // clash outlives the removal. Show the clash rather than removing —
            // the attendee asked to see it, not to go ahead.
            if (confirmed === 'learn') {
                await scheduleConflicts.maybeShow(async () => {
                    await navigateTo({
                        path: '/agenda',
                        query: { tab: 'myschedule', view: 'calendar', conflicts: 1 },
                    });
                }, { force: true });
                return;
            }
            if (!confirmed) return;
        }

        // A full limited-capacity session used to be intercepted here and refused
        // with the "already full" screen. It isn't refused any more: adding a full
        // session registers the attendee in Bizzabo, which puts them on the
        // waitlist rather than turning them away (Jack, 2026-08-19, rule 8). The
        // waitlist outcome is surfaced below, off the reservation's own response,
        // so fullness is reported by Bizzabo at the moment of the add rather than
        // guessed from a possibly-stale `is_full` before it.

        // Adding now waits on Bizzabo — every add registers (rules 3 and 6) — so
        // it is a network round trip the attendee sits through with no feedback
        // beyond a disabled button (Jack, 2026-08-20). Same persistent screen the
        // seat flow uses: abandoning it mid-flight can leave a registration
        // half-made, which is why that screen has no close affordance.
        //
        // Removals deliberately do not get one. Same round trip, but the card
        // leaves My Schedule as it goes, so the gesture already reads as doing
        // something.
        let addProcessing = null;
        const closeAddProcessing = () => {
            if (!addProcessing) return;
            addProcessing.close();
            addProcessing = null;
        };
        if (!addButtonSaved.value) {
            addProcessing = openHandle(SeatStatusModal, {
                state: 'processing',
                sessionTitle: session.title,
                // Limited capacity: this gesture adds AND claims a seat, so the
                // screen says so in that order (Nick, [Limited Capacity] [Add to
                // My Schedule]). An uncapped add has no seat to mention and keeps
                // the plain "Adding ..." copy — this modal opens for every add,
                // not just limited ones.
                ...(session.session_reservations === true
                    ? { includeAdded: true }
                    : { adding: true }),
            });
        }

        try {
            busy.value = true;
            const removing = addButtonSaved.value;
            // Read BEFORE the mutation — both are derived from state it changes.
            // Whether this session was one half of a clash decides if there is a
            // follow-up at all, and whether a seat went with it decides the copy.
            const wasConflicted = removing && scheduleConflicts.isConflicted(session.unique_id);
            const releasedSeat = seatButtonSaved.value;
            // Set inside the queued block, read after it resolves — the outcome
            // modal must not open while the mutation is still serialized.
            let seatOutcome = null;
            // Serialized (useScheduleQueue). refreshSession() assigns the session state
            // from whichever /api/_auth/session response resolves LAST, so overlapping
            // mutate→refresh chains let an early, late-resolving read overwrite
            // user.scheduled with a stale snapshot — removed sessions reappear.
            const conflict = await enqueue(async () => {
                if (removing) {
                    optimisticAdded.value = false;
                    // Release the Bizzabo registration BEFORE dropping the
                    // schedule entry. Unconditional: every add registers now, so
                    // every remove has something to release — a seat, a waitlist
                    // place, or a plain registration on an uncapped session
                    // (rules 7 and 9). Gating this on `holdsSeat` used to leave
                    // the registration standing for anything that wasn't a
                    // confirmed seat.
                    // Best-effort: a failed release must not strand the user with
                    // a session they can't remove, and it can be retried.
                    try {
                        // session.id (the catalog record id), NOT unique_id: the
                        // reservations route looks the session up by `id` and
                        // derives the Bizzabo id from it. Passing unique_id 404s.
                        await userApi.unreserve(session.id);
                    } catch (seatErr) {
                        console.error('[seat] release failed', seatErr?.message || seatErr);
                    }
                    await userApi.removeFromSchedule(session.unique_id);
                } else {
                    const res = await userApi.addToSchedule(session.unique_id);
                    // Blocked: overlaps a session already on the schedule. Don't add.
                    if (res?.conflict) return res.conflict;
                    optimisticAdded.value = true;
                    // Adding a session IS the registration (rules 3 and 6). For a
                    // limited-capacity session this is what claims the seat, and
                    // Bizzabo decides seat vs waitlist; for an ordinary session it
                    // is silent bookkeeping with no modal. Best-effort so a
                    // registration failure never blocks the schedule entry the
                    // attendee actually asked for — the seat can be claimed later
                    // from the Save a Seat button, which is what the orange
                    // action-required cards are for.
                    try {
                        seatOutcome = await userApi.reserve(session.id);
                    } catch (seatErr) {
                        console.error('[seat] register on add failed', seatErr?.message || seatErr);
                    }
                }
                // refreshSession() FIRST so user.value.scheduled — and the feed's `schedule`
                // query param — is current before the agenda reacts and refetches.
                await refreshSession();
                return null;
            });
            if (conflict) {
                // The overlap was found against the authoritative D1 schedule,
                // which can hold sessions the sealed session cookie hasn't caught
                // up on — the Schedule Builder writes D1 without updating the
                // cookie mid-stream, and only My Schedule re-hydrates. Without a
                // resync the conflict session's OWN tray reads the stale cookie
                // and shows "Add to My Schedule" instead of "Added", so the user
                // can't remove it from the link the modal points them to. Hydrate
                // from D1 first (same pattern as the agenda's My Schedule sync) so
                // the conflict session reads as scheduled everywhere. Best-effort:
                // a failed hydrate just leaves the existing cookie. (UNB Marker 662030)
                try {
                    await $fetch('/api/v1/schedule/hydrate', { method: 'POST' });
                    await refreshSession();
                } catch { /* fall back to the existing cookie */ }
                closeAddProcessing();
                doubleBook.show(session, conflict);
                return;
            }
            $bus?.emit?.('schedule:changed', { session, removed: removing });

            // Removing a conflicting session is the fix for the clash, so say
            // whether it worked (Figma section 4755:112333). Re-resolved from the
            // server rather than assumed: this session may have been in a
            // three-way group, or the attendee may have a second clash elsewhere,
            // and either leaves work still to do.
            if (wasConflicted) {
                closeAddProcessing();
                const remaining = await scheduleConflicts.loadConflictIds();
                const outcome = await open(ConflictRemovedModal, {
                    sessionTitle: session.title,
                    // Three cases, not two: pressing the schedule button on a
                    // session that also held a seat has its own wording.
                    variant: gesture === 'seat'
                        ? 'seat'
                        : (releasedSeat ? 'schedule-and-seat' : 'schedule'),
                    resolved: remaining.size === 0,
                });
                if (outcome === 'fix') {
                    await navigateTo({
                        path: '/agenda',
                        query: { tab: 'myschedule', view: 'calendar', conflicts: 1 },
                    });
                } else if (outcome === 'learn') {
                    // The primary conflict modal, per the comp's annotation.
                    await scheduleConflicts.maybeShow(async () => {
                        await navigateTo({
                            path: '/agenda',
                            query: { tab: 'myschedule', view: 'calendar', conflicts: 1 },
                        });
                    }, { force: true });
                }
                return;
            }

            // Report what the registration actually did, but only for a
            // limited-capacity session — that is the only kind where the outcome
            // is something the attendee needs to know. `includeAdded` selects the
            // copy that says the session was added AND the seat handled, which is
            // what a single add gesture now does.
            if (!removing && session.session_reservations === true && seatOutcome) {
                if (seatOutcome.key === 'saved') {
                    closeAddProcessing();
                    // NOT includeAdded: the processing screen before this already
                    // said the session was added, so repeating it here reads as a
                    // second, separate thing having happened (same ticket).
                    open(SeatStatusModal, { state: 'saved', sessionTitle: session.title, ...viewSchedule });
                    return;
                }
                if (seatOutcome.key === 'waitlisted') {
                    // Bizzabo returned 202 — the session was full, so this became a
                    // waitlist place rather than a seat. Deliberately NOT shown as
                    // a saved seat (rule 5); the card stays action-required.
                    closeAddProcessing();
                    open(SeatStatusModal, { state: 'waitlisted', sessionTitle: session.title, ...viewSchedule });
                    return;
                }

                // Adding registers too, so an add can raise the unexpected
                // double-book exactly as a seat request can. This used to be
                // swallowed: the session landed on the schedule, the card went
                // action-required, and nothing explained why — the attendee had to
                // press Save a Seat separately to find out (Jack, 2026-08-20).
                if (seatOutcome.key === 'double_book' && seatOutcome.unexpected && seatOutcome.clash) {
                    closeAddProcessing();
                    await resolveUnexpectedDoubleBook(session, seatOutcome);
                    return;
                }
            }

            // Adding a limited-capacity session while Save a Seat isn't open yet →
            // remind the user they'll need to save a seat later.
            closeAddProcessing();
            if (!removing) seatReminder.maybeShowAfterAdd(session);
        } catch (e) {
            closeAddProcessing();
            optimisticAdded.value = null;
            // A 401 means the session went stale server-side — re-sync and prompt login.
            if (e?.statusCode === 401 || e?.response?.status === 401 || e?.data?.statusCode === 401) {
                await refreshSession();
                await navigateTo('/login');
            } else {
                window.alert('There was a problem updating your schedule. Please try again.');
            }
        } finally {
            // Backstop: this screen has no close affordance, so any path that
            // reached here without closing it would trap the attendee.
            closeAddProcessing();
            busy.value = false;
        }
    };


    // ─── Save a Seat ─────────────────────────────────────────────────────────
    // Figma flows 2-5 of section 4669:55318. Until now the seat buttons rendered
    // their state (seatButtonSaved) but had NO handler at all — they were inert
    // everywhere they appeared.
    //
    // The shape is: show the processing modal, run the reservation, then swap it
    // for the outcome. `includeAdded` distinguishes Standard Add (which adds the
    // session AND saves the seat in one gesture) from an explicit Save a Seat on
    // something already scheduled.
    const handleSeat = async (e, { includeAdded = false, standbyConfirmed = false } = {}) => {
        e?.preventDefault?.();
        const session = sessionRef.value;
        if (!session) return;
        if (!loggedIn.value) {
            await navigateTo('/login');
            return;
        }
        if (busy.value) return;

        // The seat button is a TOGGLE. Pressing it while a seat is already held
        // RELEASES it; it used to register again, which Bizzabo accepts as a
        // repeat, so the press looked like it did nothing and there was no way
        // back from the very button that had just been used (Jack, 2026-08-20).
        //
        // Releasing goes through handleAdd rather than doing its own unreserve,
        // because on a limited-capacity session releasing the seat and removing
        // the session from My Schedule are ONE operation — seat-remove-confirm.vue
        // states that rule from Figma (4669:56307 / 56416), both nodes share the
        // confirm label "Remove Saved Seat & From My Schedule", and the flow
        // annotations say it holds whether or not the session is full. handleAdd
        // already implements exactly that: the confirmation (choosing the 'seat'
        // wording precisely because the seat button was the one pressed), the
        // remove-feedback path for assistant-recommended sessions, the release
        // before the schedule drop so the seat can never strand, and the
        // schedule:changed emit. Duplicating any of it here would be a second
        // copy to keep in step.
        if (seatButtonSaved.value || seatButtonWaitlisted.value) {
            if (addButtonSaved.value) {
                await handleAdd(e, { gesture: 'seat' });
                return;
            }

            // A seat held with NO schedule entry. The UI cannot produce this any
            // more (claiming a seat adds the session), but a registration made
            // directly in Bizzabo, or a schedule removal that failed after its
            // release, still can. handleAdd would read "not saved" here and ADD
            // the session — the opposite of what was asked — so release on its
            // own instead. There is no schedule entry to remove, so the rule the
            // confirmation states is not violated.
            const confirmed = await open(SeatRemoveConfirmModal, {
                sessionTitle: session.title,
                variant: seatButtonWaitlisted.value ? 'standby' : 'seat',
            });
            if (!confirmed) return;

            try {
                busy.value = true;
                // Serialized like every other mutation: refreshSession() is
                // last-RESOLVED-wins, so an overlapping chain can reinstate a
                // stale snapshot.
                await enqueue(async () => {
                    // session.id, not unique_id — as in the release above.
                    await userApi.unreserve(session.id);
                    await refreshSession();
                });
            } catch (err) {
                if (err?.statusCode === 401 || err?.response?.status === 401 || err?.data?.statusCode === 401) {
                    await refreshSession();
                    await navigateTo('/login');
                } else {
                    // This IS the whole action here, so a silent failure would
                    // leave the seat held with the button still claiming so.
                    open({ headline: 'We could not remove your saved seat', body: 'Please try again in a moment.' });
                }
            } finally {
                busy.value = false;
            }
            return;
        }

        // A CLASH BLOCKS THE RESERVATION (Nick, Asana 1217708959827728).
        //
        // Placed after the release branches above on purpose: giving a held seat
        // up is one of the ways a clash gets settled, so that has to keep working
        // on a conflicting session. Only claiming a NEW seat is refused.
        //
        // Refused rather than warned about, because a seat on a session the
        // attendee cannot attend is worse than none — it also takes the seat from
        // someone who could. The modal explains what to clear first and offers
        // the two ways forward the comps give.
        if (scheduleConflicts.conflictIds?.value?.has(session.unique_id)) {
            // Same rule as the removal confirmation: a clash needs at least two
            // items, so exactly two conflicted items anywhere means removing this
            // one settles the schedule.
            const choice = await open(SeatConflictBlockedModal, {
                sessionTitle: session.title,
                resolves: scheduleConflicts.conflictIds.value.size === 2,
            });

            if (choice === 'learn') {
                await scheduleConflicts.maybeShow(async () => {
                    await navigateTo({
                        path: '/agenda',
                        query: { tab: 'myschedule', view: 'calendar', conflicts: 1 },
                    });
                }, { force: true });
            } else if (choice === 'remove') {
                // The ordinary removal path. No seat is held — that is why we are
                // here — so it has nothing to release and raises no second
                // confirmation; it simply takes the session off the schedule.
                await handleAdd(e, { gesture: 'schedule' });
            }
            return;
        }

        // ASK BEFORE JOINING STANDBY.
        //
        // A full session can only be joined as a standby place, and joining is a
        // real write against the attendee's Bizzabo ticket that they then have to
        // undo deliberately. Pressing a button labelled "Join Waitlist" is an
        // intent to ask, not a decision made — so the same screen that explains
        // the session is full now carries the affirmative, and nothing is sent
        // until it comes back.
        //
        // Only when they hold NOTHING: with a seat or a place already, this
        // gesture is a removal and has its own confirmation.
        // UNB-751: this branch is the ONLY place we know a waitlist place is what
        // is being asked for before Bizzabo answers. It decides with 200 vs 202,
        // which arrives after the processing screen is already up — so a session
        // that fills between load and press still shows the seat wording. That is
        // the honest limit: we say what was ASKED for, not what will come back.
        //
        // `standbyConfirmed` means the caller ALREADY put this screen in front of
        // them and is passing the answer through — the add gesture does exactly
        // that. Asking again would show the same modal a second time and demand
        // the answer just given, so the block is skipped and the intent carried
        // forward into the wording below.
        let joiningWaitlist = standbyConfirmed;
        if (!standbyConfirmed
            && sessionRef.value?.is_full === true
            && !seatButtonSaved.value
            && !seatButtonWaitlisted.value) {
            // A CLASH OUTRANKS THE FULL EXPLANATION here too (Bri via Marker.io,
            // UNB-756). The add path has applied this since Nick's ruling
            // (Asana 1217708959827710); the seat path did not, so pressing Join
            // Waitlist on a session that also overlapped something showed the
            // waitlist confirmation FIRST and the double-book only afterwards —
            // the attendee agreed to join a list for a session they then learnt
            // they could not attend.
            //
            // Only when this gesture would ADD the session: a clash cannot exist
            // for one already on the schedule, because the add that put it there
            // was itself refused on overlap. Skipping the check there also saves
            // a round trip on the common path.
            //
            // Checked rather than inferred from the add, because the add happens
            // AFTER the confirmation — which is the whole bug. If the check
            // itself fails we fall through to the full-session modal: the add
            // behind it re-runs the same test server-side and still refuses, so
            // the clash is caught either way and the worst case is the ordering
            // we had before.
            if (!addButtonSaved.value) {
                let clash = null;
                try {
                    busy.value = true;
                    ({ conflict: clash } = await userApi.checkConflict(session.unique_id));
                } catch {
                    clash = null;
                } finally {
                    busy.value = false;
                }
                if (clash) {
                    doubleBook.show(session, clash);
                    return;
                }
            }

            const choice = await open(SeatFullModal, {
                sessionTitle: session.title,
                variant: 'seat-failed',
                alreadyScheduled: addButtonSaved.value,
            });
            if (choice !== 'join-standby') {
                // 'find-session' and 'add-to-schedule' are the other two ways out
                // of that screen, and closing it is a decision too — none of them
                // may fall through into the reservation.
                await handleSeatFullChoice(choice, session);
                return;
            }
            joiningWaitlist = true;
        }

        // Whether the session was already on the schedule decides both whether we
        // add it below and which copy the outcome uses: "seat saved" on its own,
        // or "added to your schedule and seat saved" when this one gesture did
        // both. An explicit includeAdded from a caller still wins.
        const wasScheduled = addButtonSaved.value;
        const addedByThisGesture = includeAdded || !wasScheduled;

        // Persistent while in flight — the comp gives this screen no close
        // affordance, because abandoning it can strand the reservation.
        const processing = openHandle(SeatStatusModal, {
            state: 'processing',
            sessionTitle: session.title,
            // Same rule as includeAdded below, applied to the other half: the
            // screen reports what the attendee ASKED for. They pressed Join
            // Waitlist, so it says waitlist, even though the session is added
            // along the way (UNB-751).
            joiningWaitlist,
            // Deliberately NOT includeAdded (Nick, [Limited Capacity] [Save a
            // Seat]): pressing Save a Seat asks for a seat, so that is what this
            // screen reports, even though the session is added along the way. The
            // SAVED screen that follows still says both — that one he asked to
            // leave alone.
        });

        try {
            busy.value = true;
            // Serialized with adds/removes for the same reason those are: the
            // session refresh is last-RESOLVED-wins, so an overlapping chain can
            // reinstate a stale snapshot.
            const result = await enqueue(async () => {
                // Saving a seat for something not yet on the schedule adds it too.
                // A seat without a schedule entry cannot be given back — rule 9
                // releases the registration when the session is REMOVED from the
                // schedule, and there is nothing there to remove — so the seat
                // strands until someone calls the API by hand. The modal copy has
                // always said "added to your schedule and seat saved"; only the
                // add was missing.
                if (!wasScheduled) {
                    const addRes = await userApi.addToSchedule(session.unique_id);
                    // An overlap blocks the add, and a seat for a session the
                    // attendee could not add would strand exactly as above.
                    if (addRes?.conflict) return { conflict: addRes.conflict };
                    optimisticAdded.value = true;
                }
                // session.id, not unique_id — see the note on unreserve above.
                const res = await userApi.reserve(session.id);
                await refreshSession();
                return res;
            });

            if (result?.conflict) {
                processing.close();
                doubleBook.show(session, result.conflict);
                return;
            }

            processing.close();

            // `key` is the stable discriminator added to the reservation
            // messages — branching on title/copy would break the first time
            // anyone edits the wording.
            if (result?.key === 'saved') {
                open(SeatStatusModal, { state: 'saved', sessionTitle: session.title, includeAdded: addedByThisGesture, ...viewSchedule });
                $bus?.emit?.('schedule:changed', { session, removed: false });
                return;
            }

            if (result?.key === 'waitlisted') {
                // Bizzabo returned 202: the session was full, so the request
                // became a waitlist entry rather than a seat.
                open(SeatStatusModal, { state: 'waitlisted', sessionTitle: session.title, ...viewSchedule });
                $bus?.emit?.('schedule:changed', { session, removed: false });
                return;
            }

            if (result?.key === 'capacity') {
                // Covers both "already full" and the race where it fills DURING
                // processing; the comp lands both on this screen.
                //
                // AWAITED: the modal resolves which way out was taken, and both
                // of its buttons are actions. Opening it without awaiting left
                // "Find New Session" and "Add to My Schedule" inert — they
                // closed the dialog and did nothing.
                const choice = await open(SeatFullModal, {
                    sessionTitle: session.title,
                    // Reaching here from the seat button means the session is
                    // already on the schedule, so the "add it anyway" way out is
                    // already done. handleSeatFullChoice no-ops it regardless;
                    // this stops it being offered in the first place.
                    alreadyScheduled: addButtonSaved.value,
                    variant: 'seat-failed',
                });
                await handleSeatFullChoice(choice, session);
                return;
            }

            // Bizzabo refused outright: the session accepts no reservations,
            // because it was flagged reservations-required in Sessionboard while
            // capacity was never enabled on the Bizzabo side.
            //
            // This is what the 'already-full' screen now exists for. Rule 8 took
            // away its original trigger — adding a full session no longer gets
            // refused, it waitlists — leaving the variant with no caller at all.
            // Repointing it here (Jack, 2026-08-19) is a better fit than the
            // generic error it used to get: both exits this screen offers are the
            // attendee's actual options, since no seat is coming. Awaited for the
            // same reason as the capacity case — both buttons are actions, and
            // opening it without awaiting leaves them inert.
            if (result?.key === 'double_book' && result?.unexpected && result?.clash) {
                await resolveUnexpectedDoubleBook(session, result);
                return;
            }

            if (result?.key === 'not_reservable') {
                const choice = await open(SeatFullModal, {
                    sessionTitle: session.title,
                    // Reaching here from the seat button means the session is
                    // already on the schedule, so the "add it anyway" way out is
                    // already done. handleSeatFullChoice no-ops it regardless;
                    // this stops it being offered in the first place.
                    alreadyScheduled: addButtonSaved.value,
                    variant: 'already-full',
                });
                await handleSeatFullChoice(choice, session);
                return;
            }

            // Everything else (closed, vip_only, hubspotter, invalid,
            // double_book) is already written as a title+copy warning by the
            // endpoint, so it goes to the generic modal rather than being
            // re-worded here.
            open({ headline: result?.title || 'We could not save your seat', body: result?.copy || '' });
        } catch (err) {
            processing.close();
            if (err?.statusCode === 401 || err?.response?.status === 401 || err?.data?.statusCode === 401) {
                await refreshSession();
                await navigateTo('/login');
            } else {
                open({ headline: 'We could not save your seat', body: 'Please try again in a moment.' });
            }
        } finally {
            busy.value = false;
        }
    };

    return { optimisticAdded, busy, isScheduled, addButtonSaved, seatButtonSaved, seatButtonWaitlisted, handleAdd, handleSeat };
}
